import * as React from 'react';
import { connect, Dispatch } from 'react-redux';
import l10n from '@built/i18n';

import SetupMfa, { MfaType } from '../components/SetupMfa';
import {
  ToastOpts,
  enableMfa,
  resendSmsCode,
  setPhoneNumber,
  showToast,
  verifyPhoneNumber,
  verifySoftwareToken,
  setAuthError
} from '../actions';
import { AuthError, StoreState, SetupState, AuthErrorType } from '../types';
import {
  getAuthError,
  getUsername,
  getPhoneNumber,
  getPhoneNumberVerified
} from '../reducers';
import getAuthModule from '../auth';

const authModule = getAuthModule();

const DEFAULT_MFA_TYPE = 'sms';

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  class SetupMfaContainer extends React.Component<
    SetupMfaContainerProps,
    SetupMfaContainerState
  > {
    constructor(props: SetupMfaContainerProps) {
      super(props);
      this.state = {
        codeOrPhone: '',
        finished: false,
        fullPhoneNumber: '',
        showPhoneError: false,
        type: DEFAULT_MFA_TYPE
      };

      // get our software token
      authModule.getTokenSecret().then(secret => {
        this.setState({ totpSecret: secret });
      });
    }

    componentWillReceiveProps(newProps: SetupMfaContainerProps) {
      if (
        (!newProps.authError && this.props.authError) ||
        (newProps.phoneNumber && !this.props.phoneNumber)
      ) {
        this.setState({ codeOrPhone: '' });
      }
    }

    render() {
      const {
        authError,
        phoneNumber,
        phoneNumberVerified,
        username
      } = this.props;
      const {
        busy,
        codeOrPhone,
        finished,
        phoneNumberValid,
        showPhoneError,
        totpSecret,
        type
      } = this.state;

      return (
        <SetupMfa
          busy={busy}
          codeOrPhone={codeOrPhone || ''}
          type={type || ''}
          authError={authError}
          mfaComplete={finished}
          mfaSecret={totpSecret}
          username={username}
          phoneNumber={phoneNumber}
          phoneNumberVerified={phoneNumberVerified === 'true'}
          phoneNumberValid={phoneNumberValid}
          showPhoneError={showPhoneError}
          onTypeChange={this.handleTypeChange}
          onSubmitPhoneNumber={this.handleSubmitPhoneNumber}
          onSubmitSmsCode={this.handleSubmitSmsCode}
          onSubmitTotpCode={this.handleSubmitTotpCode}
          onResendSmsCode={this.handleResendSmsCode}
          onCodeChange={this.handleCodeChange}
          onPhoneChange={this.handlePhoneChange}
          onComplete={this.handleComplete}
        />
      );
    }

    handleTypeChange = async (type: MfaType) => {
      // reset auth errors on tab change
      this.props.setAuthError();

      this.setState({
        busy: false,
        codeOrPhone: '',
        showPhoneError: false,
        phoneNumberValid: false,
        type
      });
    };

    handleCodeChange = (event: React.SyntheticEvent<HTMLInputElement>) => {
      const value = event.currentTarget.value;
      this.setState({ codeOrPhone: value });
    };

    /**
     * Handler for when the international phone number component
     * value changes. We use this to determine if the provided
     * phone number is valid and the "full" phone number including
     * country code.
     */
    handlePhoneChange = (
      isValid: boolean,
      newNumber: string,
      countryData: {},
      fullNumber: string
    ): void => {
      this.setState({
        codeOrPhone: newNumber,
        phoneNumberValid: newNumber.length > 0 ? isValid : undefined,
        showPhoneError: false,
        fullPhoneNumber: fullNumber
      });
    };

    handleComplete = () => {
      this.setState({ busy: true });
      this.props.updateState(SetupState.completedMfa);
    };

    handleResendSmsCode = async () => {
      try {
        this.setState({ busy: true });

        const success = await this.props.resendSmsCode();
        if (success) {
          const last4 = this.props.phoneNumber.slice(
            this.props.phoneNumber.length - 4
          );
          this.props.showToast(
            l10n.t('SetupMfa:toaster.resendVerificationCodeMessage', {
              defaultValue:
                'A new verification code was sent to the number ending in {{last4}}.',
              last4
            })
          );
        }
      } catch (error) {
        // intentionally ignore error
      }
      this.setState({ busy: false });
    };

    /**
     * Submits the user-provided phone number as the `phone_number`
     * attribute on their user profile; this will also set the
     * `AuthData.phoneNumber` state causing a rerender to accept
     * the verification code
     */
    handleSubmitPhoneNumber = async (
      event?: React.SyntheticEvent<HTMLFormElement>
    ) => {
      if (event) {
        event.preventDefault();
      }

      const { fullPhoneNumber, phoneNumberValid } = this.state;

      // if phone number is invalid (also includes empty phone number)
      // we provide inline validation error
      if (!phoneNumberValid) {
        this.setState({ showPhoneError: true });
        return;
      }

      // strip all phone number formatting
      const phoneNumber = fullPhoneNumber.replace(/[^0-9\+]/g, '');

      this.setState({ busy: true });
      try {
        await this.props.setPhoneNumber(phoneNumber);
      } catch (error) {
        // intentionally ignore error
      }
      this.setState({ busy: false });
    };

    /**
     * Verifies the SMS-based verification code
     */
    handleSubmitSmsCode = async (
      event?: React.SyntheticEvent<HTMLFormElement>
    ) => {
      if (event) {
        event.preventDefault();
      }
      const { codeOrPhone } = this.state;

      // if code is missing, we should bail out early and not
      // submit a request to cognito
      if (!codeOrPhone) {
        this.props.setAuthError({
          type: AuthErrorType.MFA_VERIFICATION_ERROR,
          message: l10n.t(
            'SetupMfa:Common.errors.missingVerificationCode',
            'Please provide a verification code.'
          )
        });
        return;
      }

      this.setState({ busy: true });
      let finished = false;
      try {
        finished = await this.props.verifyPhoneNumber(codeOrPhone);
      } catch (error) {
        finished = false;
      }
      this.setState({ busy: false, codeOrPhone, finished });
    };

    /**
     * Verifies the TOTP-based verification code
     */
    handleSubmitTotpCode = async (
      event?: React.SyntheticEvent<HTMLFormElement>
    ) => {
      if (event) {
        event.preventDefault();
      }

      const { codeOrPhone } = this.state;

      // if code is missing, we should bail out early and not
      // submit a request to cognito
      if (!codeOrPhone) {
        this.props.setAuthError({
          type: AuthErrorType.MFA_VERIFICATION_ERROR,
          message: l10n.t(
            'SetupMfa:Common.errors.missingVerificationCode',
            'Please provide a verification code.'
          )
        });
        return;
      }

      this.setState({ busy: true });
      let finished = false;
      try {
        finished = await this.props.verifySoftwareToken(codeOrPhone);
      } catch (error) {
        finished = false;
      }

      this.setState({ busy: false, codeOrPhone, finished });
    };
  }
);

export interface SetupMfaContainerProps {
  authError?: AuthError;
  username: string;
  phoneNumber: string;
  phoneNumberVerified: 'true' | 'false';
  enableMfa(type: MfaType): Promise<boolean>;
  resendSmsCode(): Promise<boolean>;
  setAuthError(authError?: AuthError): void;
  setPhoneNumber(phoneNumber: string): Promise<boolean>;
  showToast(message: string, opts?: ToastOpts): void;
  updateState(newState: SetupState): Promise<boolean>;
  verifyPhoneNumber(code: string): Promise<boolean>;
  verifySoftwareToken(code: string): Promise<boolean>;
}

export interface SetupMfaContainerState {
  /** Whether to indicate that the app is processing */
  busy?: boolean;

  /** The type of MFA being set up */
  type: 'sms' | 'totp';

  /** The value of user-provided input that represents a verification
   * code or a phone number depending on the global phoneNumber state
   */
  codeOrPhone: string;

  /** Tracks if the provided phone number is considered valid or not */
  phoneNumberValid?: boolean;

  /** Whether to show phone number errors or not */
  showPhoneError: boolean;

  /** The complete international phone number */
  fullPhoneNumber: string;

  /** The secret to use to display a QR code for totp setup */
  totpSecret?: string;

  /** To track whether MFA setup is finished or not */
  finished: boolean;
}

function mapStateToProps(
  state: StoreState,
  _ownProps?: Pick<SetupMfaContainerProps, 'updateState'>
): Pick<
  SetupMfaContainerProps,
  'authError' | 'phoneNumber' | 'phoneNumberVerified' | 'username'
> {
  return {
    authError: getAuthError(state),
    username: getUsername(state) || '',
    phoneNumber: getPhoneNumber(state) || '',
    phoneNumberVerified: getPhoneNumberVerified(state) || 'false'
  };
}

function mapDispatchToProps(
  dispatch: Dispatch<StoreState>
): Pick<
  SetupMfaContainerProps,
  | 'enableMfa'
  | 'resendSmsCode'
  | 'verifySoftwareToken'
  | 'setAuthError'
  | 'setPhoneNumber'
  | 'showToast'
  | 'verifyPhoneNumber'
> {
  return {
    enableMfa(type: MfaType) {
      return dispatch(enableMfa(type));
    },
    resendSmsCode() {
      return dispatch(resendSmsCode());
    },
    setAuthError(authError: AuthError) {
      return dispatch(setAuthError(authError));
    },
    setPhoneNumber(phoneNumber: string) {
      return dispatch(setPhoneNumber(phoneNumber));
    },
    showToast(message: string, opts?: ToastOpts) {
      return dispatch(showToast(message, opts));
    },
    verifyPhoneNumber(code: string) {
      return dispatch(verifyPhoneNumber(code, true));
    },
    verifySoftwareToken(code: string) {
      return dispatch(verifySoftwareToken(code, true));
    }
  };
}



// WEBPACK FOOTER //
// ./src/containers/SetupMfa.tsx